import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { PERMISSIONS } from '@lims/permissions';
import { clientsApi, invoicesApi, settingsApi } from '../../api/endpoints.js';
import { useAuth } from '../../app/auth-context.js';
import {
  ActionBar,
  ActionButton,
  DateField,
  Form,
  FormSection,
  NumberField,
  PageHeader,
  Panel,
  SelectField,
  TextAreaField,
  useToast,
} from '../../components/ui/index.js';
import { currentLocale } from '../../i18n/index.js';
import { FinanceMutationError } from './finance-errors.js';
import {
  InvoiceLineEditor,
  lineFromService,
  linePayload,
  type LineDraft,
} from './InvoiceLineEditor.js';

/**
 * Open a draft invoice.
 *
 * DUPLICATE BILLING IS THE BACKEND'S RULE (D150), never this page's: a
 * service already fully billed is not offered again in the picker, but the
 * authoritative refusal is `409 DUPLICATE_BILLING` from the server, shown as
 * what it is.
 */
export function InvoiceFormPage() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { can } = useAuth();
  const { show } = useToast();
  const locale = currentLocale();

  const canOverride = can(PERMISSIONS.PRICING_OVERRIDE);

  const [clientId, setClientId] = useState<string | null>(
    searchParams.get('clientId'),
  );
  const [documentDiscountPercent, setDocumentDiscountPercent] = useState<
    number | null
  >(null);
  const [documentDiscountReason, setDocumentDiscountReason] = useState('');
  const [dueDate, setDueDate] = useState<string | null>(null);
  const [clientNote, setClientNote] = useState('');
  const [internalNote, setInternalNote] = useState('');
  const [lines, setLines] = useState<LineDraft[]>([]);
  const [pickedServiceId, setPickedServiceId] = useState<string | null>(null);
  const [seeded, setSeeded] = useState(false);

  const clients = useQuery({
    queryKey: ['clients', 'selectable'],
    queryFn: () => clientsApi.list({ pageSize: 100, includeSuspended: false }),
  });

  const settings = useQuery({
    queryKey: ['settings'],
    queryFn: settingsApi.get,
    enabled: can(PERMISSIONS.SETTINGS_VIEW),
  });

  const billable = useQuery({
    queryKey: ['billable-services', 'for-client', clientId],
    queryFn: () =>
      invoicesApi.billableServices({ clientId: clientId as string, pageSize: 200 }),
    enabled: Boolean(clientId),
  });

  // Seed the lines once from the query string's `serviceIds`, as soon as the
  // client's billable services have loaded.
  const preselectedIds = useMemo(
    () => (searchParams.get('serviceIds') ?? '').split(',').filter(Boolean),
    [searchParams],
  );
  if (!seeded && billable.data && preselectedIds.length > 0) {
    const matched = billable.data.data.filter((service) =>
      preselectedIds.includes(service.id),
    );
    if (matched.length > 0) {
      setLines(matched.map((service) => lineFromService(service, locale)));
    }
    setSeeded(true);
  } else if (!seeded && billable.data && preselectedIds.length === 0) {
    setSeeded(true);
  }

  const usedServiceIds = new Set(
    lines.map((line) => line.workOrderServiceId).filter(Boolean),
  );
  const availableServices = (billable.data?.data ?? []).filter(
    (service) => !usedServiceIds.has(service.id),
  );

  const save = useMutation({
    mutationFn: () =>
      invoicesApi.create({
        clientId,
        lines: linePayload(lines),
        ...(documentDiscountPercent === null
          ? {}
          : { documentDiscountPercent }),
        ...(documentDiscountReason.trim()
          ? { documentDiscountReason: documentDiscountReason.trim() }
          : {}),
        ...(dueDate ? { dueDate } : {}),
        ...(clientNote.trim() ? { clientNote: clientNote.trim() } : {}),
        ...(internalNote.trim() ? { internalNote: internalNote.trim() } : {}),
      }),
    onSuccess: async (created) => {
      await queryClient.invalidateQueries({ queryKey: ['invoices'] });
      show({ tone: 'success', message: t('invoices.created') });
      navigate(`/invoices/${created.id}`, { replace: true });
    },
  });

  const currency = billable.data?.data[0]?.currency ?? settings.data?.currency;

  return (
    <>
      <PageHeader
        title={t('invoices.createTitle')}
        breadcrumbs={[
          { label: t('nav.invoices'), to: '/invoices' },
          { label: t('invoices.createTitle') },
        ]}
      />

      <div className="mb-stack">
        <FinanceMutationError error={save.error} />
      </div>

      {/* A form reads top to bottom: one capped column, as on every form page. */}
      <div className="w-full max-w-4xl">
        <Form
          onSubmit={() => save.mutate()}
          isSubmitting={save.isPending}
          actions={
            <div className="border-t border-default-200 pt-stack">
              <ActionBar>
                <ActionButton priority="tertiary" onPress={() => navigate('/invoices')}>
                  {t('common.cancel')}
                </ActionButton>
                <ActionButton
                  priority="primary"
                  type="submit"
                  isLoading={save.isPending}
                  isDisabled={!clientId || lines.length === 0}
                >
                  {t('common.save')}
                </ActionButton>
              </ActionBar>
            </div>
          }
        >
          <FormSection title={t('invoices.client')} columns={1}>
            <SelectField
              label={t('invoices.client')}
              placeholder={t('invoices.selectClient')}
              value={clientId}
              onChange={(value) => {
                setClientId(value);
                setLines([]);
                setSeeded(false);
              }}
              isRequired
              options={(clients.data?.data ?? []).map((client) => ({
                value: client.id,
                label: client.name ?? client.clientNumber,
                description: client.clientNumber,
              }))}
            />
          </FormSection>

          <FormSection title={t('common.details')}>
            <DateField
              label={t('invoices.dueDate')}
              description={t('invoices.dueDateHint')}
              value={dueDate}
              onChange={setDueDate}
            />
          </FormSection>

          <Panel title={t('invoices.lines')}>
            <InvoiceLineEditor
              lines={lines}
              setLines={setLines}
              availableServices={availableServices}
              pickedServiceId={pickedServiceId}
              setPickedServiceId={setPickedServiceId}
              currency={currency}
              canOverride={canOverride}
              locale={locale}
            />
          </Panel>

          <FormSection
            title={t('invoices.documentDiscount')}
            description={t('invoices.documentDiscountHint')}
          >
            <NumberField
              label={t('invoices.discountPercent')}
              value={documentDiscountPercent}
              onChange={setDocumentDiscountPercent}
              step="0.01"
            />
            <TextAreaField
              label={t('invoices.discountReason')}
              value={documentDiscountReason}
              onChange={setDocumentDiscountReason}
              rows={2}
            />
          </FormSection>

          <FormSection title={t('invoices.notes')}>
            <TextAreaField
              label={t('invoices.clientNote')}
              description={t('invoices.clientNoteHint')}
              value={clientNote}
              onChange={setClientNote}
              rows={2}
            />
            <TextAreaField
              label={t('invoices.internalNote')}
              description={t('invoices.internalNoteHint')}
              value={internalNote}
              onChange={setInternalNote}
              rows={2}
            />
          </FormSection>
        </Form>
      </div>
    </>
  );
}
